/**
 * Step 1 - what kind of notification this is.
 *
 * A single bordered block of rows sharing hairlines, rather than seven
 * free-floating cards each with its own border, shadow and oversized emoji. The
 * row is the unit an operator scans: icon for recognition, name, one line of
 * consequence, and the lane tag so the cost of the choice is visible before it
 * is made rather than three steps later.
 *
 * Selection is marked with a gold rule and an inverted icon chip - strong enough
 * to be unambiguous at a glance, quiet enough that seven rows still read as one
 * component.
 */
import type { CampaignKind } from '../constants'

type Props = {
  kinds: readonly CampaignKind[]
  selectedKey: string
  onSelect: (kind: CampaignKind) => void
}

export default function CampaignTypePicker({ kinds, selectedKey, onSelect }: Props) {
  return (
    <div className="ntf-types" role="radiogroup" aria-label="Notification type">
      {kinds.map((kind) => {
        const Icon = kind.icon
        const selected = kind.key === selectedKey
        return (
          <button
            key={kind.key}
            type="button"
            role="radio"
            aria-checked={selected}
            className={`ntf-type${selected ? ' is-selected' : ''}`}
            onClick={() => onSelect(kind)}
          >
            <span className="ntf-type__icon" aria-hidden>
              <Icon size={15} strokeWidth={1.75} />
            </span>
            <span style={{ minWidth: 0 }}>
              <span className="ntf-type__label">
                {kind.label}
                <span className="ntf-type__lane">{kind.lane}</span>
              </span>
              <span className="ntf-type__blurb" style={{ display: 'block' }}>
                {kind.blurb}
              </span>
            </span>
          </button>
        )
      })}
    </div>
  )
}
